'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from '@/components/ui/Toasts/use-toast';
import { Button } from '../Button/Button';
import { Share, Download, Bookmark, MessageCircle, Plus } from 'lucide-react';
import { ToneAndChildren, ModelWithChildren } from '@/utils/helpers';
import { formatCount } from '@/utils/format-count';
import { useState } from 'react';
import LoadingDots from '../LoadingDots';
import { createClient } from '@/utils/supabase/client';
import { downloadToneModels, isIOS } from '@/utils/download';
import { copyToClipboard } from '@/utils/clipboard';
import { DownloadButton } from '../DownloadButtonModal/DonwloadButtonModal';
import { GearUnsupportedDialog } from '../Select/GearUnsupportedDialog';
import { NoA2ModelsModal } from './NoA2ModelsModal';
import {
  FirstA2DownloadModal,
  shouldShowFirstA2Modal,
} from '../Models/FirstA2DownloadModal';
import { usePluginDownloadIntercept } from '../PluginDownloadModal/PluginDownloadModal';
import { ARCHITECTURE_VERSION, type ArchitectureVersion } from '@/utils/constants';
import Link from 'next/link';
import { analytics } from '@/lib/analytics';
// import DownloadUxModal from '../DownloadUxModal/DownloadUxModal';

const secondaryIconClass = 'w-6 h-6 flex-shrink-0';
const secondaryItemClass = 'flex items-center gap-2 text-white text-base whitespace-nowrap';

const GuardedAction = ({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) => (
  <div className={disabled ? 'opacity-40' : undefined} onClick={onClick}>
    <div className={disabled ? 'pointer-events-none' : undefined}>{children}</div>
  </div>
);

interface Props {
  isOwner: boolean;
  tone: ToneAndChildren;
  userId: string;
  isFavorite: boolean;
  isPrivate: boolean;
  /** Share token fetched SSR; used to build a shareable URL for private tones. */
  toneToken?: string;
  isApiSelect?: boolean;
  isOAuthFlow?: boolean;
  isGearUnsupported?: boolean;
  selectGears?: string[];
  /** OAuth flow requested architecture A2 but this tone has no A2 models (tones_counts.has_a2_models). */
  isArchitectureUnsupported?: boolean;
  /**
   * Core-site flag: NAM tone with no A2 models. Disables the blue button and
   * routes its click to the No-A2 modal. Independent of segmented-control state.
   */
  noA2Nam?: boolean;
  /**
   * Architecture to filter the downloaded model set by. Core NAM passes '2'
   * (the blue button is A2-hardcoded). Select and IR leave undefined — the
   * OAuth/embed branches in `downloadToneModels` don't bundle files anyway.
   */
  architectureFilter?: ArchitectureVersion;
}

const Actions = ({
  isOwner,
  tone,
  userId,
  isFavorite: ssrIsFavorite,
  isPrivate,
  toneToken,
  isApiSelect = false,
  isOAuthFlow = false,
  isGearUnsupported = false,
  isArchitectureUnsupported = false,
  selectGears,
  noA2Nam = false,
  architectureFilter,
}: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);
  const [showNoA2Modal, setShowNoA2Modal] = useState(false);
  const [showFirstA2Modal, setShowFirstA2Modal] = useState(false);
  const pluginDownload = usePluginDownloadIntercept(!isApiSelect);
  const supabase = createClient();
  const [downloadCount, setDownloadCount] = useState(tone.downloads_count || 0);
  const [favoriteCount, setFavoriteCount] = useState(tone.favorites_count || 0);
  const [isFavorite, setIsFavorite] = useState(ssrIsFavorite);
  const [downloaded, setDownloaded] = useState(false); // dumb state to prevent people from spoofing the download count
  // const [showDownloadUxModal, setShowDownloadUxModal] = useState(false);

  const downloadModels = async () => {
    if (architectureFilter === ARCHITECTURE_VERSION.A2 && shouldShowFirstA2Modal()) {
      setShowFirstA2Modal(true);
    }
    setIsDownloading(true);
    try {
      await downloadToneModels({
        tone,
        architectureFilter,
        onSuccess: async () => {
          const data = {
            tone_id: tone.id,
          } as { tone_id: number | null; user_id?: string };
          if (userId) {
            data.user_id = userId;
          }
          if (!downloaded) {
            try {
              const response = await fetch('/api/downloads', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
              });

              // ✅ IMPORTANT: Consume the response body
              if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to record download:', errorData);
              } else {
                // Even if you don't need the data, consume the body
                await response.json();
              }
            } catch (error) {
              console.error('Failed to record download:', error);
            }
            setDownloadCount(downloadCount + 1);
            setDownloaded(true);
            // only refresh router if not on iOS
            if (!isIOS() && !isApiSelect) {
              router.refresh();
            }
          }
          // setShowDownloadUxModal(true);
        },
        isOAuthFlow: isApiSelect ? isOAuthFlow : false,
        authorizationId: searchParams?.get('authorization_id') ?? null,
      });
    } catch (error) {
      console.error('Failed to download models:', error);
      alert(`Failed to download models: ${error}`);
    }
    setIsDownloading(false);
  };

  const favoriteTone = async () => {
    if (!userId) {
      // redirect to login
      router.push('/signin/email');
      return;
    }
    if (isFavorite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('tone_id', tone.id!)
        .eq('user_id', userId);
      if (error) {
        console.error('Failed to delete favorite:', error);
      }
    } else {
      const { error } = await supabase.from('favorites').insert({
        tone_id: tone.id,
        user_id: userId,
      });
      if (error) {
        console.error('Failed to insert favorite:', error);
      }
    }
    setFavoriteCount(favoriteCount + (isFavorite ? -1 : 1));
    setIsFavorite(!isFavorite);
    router.refresh();
  };

  const copyUrl = async () => {
    const searchToken = searchParams.get('token');
    try {
      // The token is provided via props (fetched SSR), so the URL is built
      // synchronously. This keeps navigator.clipboard.writeText within the
      // user-gesture activation, which WebKit/Safari require.
      const urlToCopy =
        isPrivate && !window.location.href.includes('token=')
          ? `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}token=${searchToken || toneToken}`
          : window.location.href;

      await copyToClipboard(urlToCopy);

      toast({
        title: 'URL copied',
        description: 'The URL has been copied to your clipboard',
      });
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: 'Failed to copy URL',
        description: 'An error occurred while trying to copy the URL to your clipboard',
        variant: 'destructive',
      });
    }
  };
  const selectDialogActive = isGearUnsupported || isArchitectureUnsupported;
  const disabled = selectDialogActive || noA2Nam;
  const commentsCount = tone.visible_comments_count || 0;
  const primaryLabel = isApiSelect ? 'Select Tone' : 'Download Tone';
  const shareLabel = isPrivate ? 'Private Share' : 'Share';

  const onGuardedClick = selectDialogActive
    ? () => setShowUnsupportedDialog(true)
    : noA2Nam
      ? () => setShowNoA2Modal(true)
      : undefined;

  return (
    <div className="flex flex-col gap-8">
      {/* {showDownloadUxModal && <DownloadUxModal userId={userId} />} */}
      {selectDialogActive && showUnsupportedDialog && (
        <GearUnsupportedDialog
          selectGears={selectGears ?? []}
          isArchitectureUnsupported={isArchitectureUnsupported}
          onClose={() => setShowUnsupportedDialog(false)}
        />
      )}
      {noA2Nam && (
        <NoA2ModelsModal open={showNoA2Modal} onClose={() => setShowNoA2Modal(false)} />
      )}
      <FirstA2DownloadModal
        open={showFirstA2Modal}
        onClose={() => setShowFirstA2Modal(false)}
      />
      {pluginDownload.modal}
      <DownloadButton
        tone={tone}
        tracking={{
          event: 'tone.downloaded',
          properties: {
            from: 'actions',
            isApiSelect,
          },
        }}
        onDownload={downloadModels}
        isDownloading={isDownloading}
        downloadCount={downloadCount}
        isApiSelect={isApiSelect}
        renderTrigger={({ onClick, isDownloading: downloading, disabled: noModels }) => (
          <>
            <GuardedAction disabled={disabled} onClick={onGuardedClick}>
              <Button
                className="w-full sm:w-[256px] min-h-[50px]"
                variant="slim"
                isPrimary
                disabled={noModels || downloading}
                onClick={() => pluginDownload.intercept(onClick)}
                aria-label={primaryLabel}
              >
                {downloading ? (
                  <LoadingDots />
                ) : (
                  <>
                    {isApiSelect ? <Plus size={20} /> : <Download size={20} />}
                    {primaryLabel}
                  </>
                )}
              </Button>
            </GuardedAction>
            <div className="flex w-full items-center justify-between sm:justify-start gap-6 sm:gap-8">
              <GuardedAction disabled={disabled} onClick={onGuardedClick}>
                <button
                  type="button"
                  className={secondaryItemClass}
                  onClick={() => pluginDownload.intercept(onClick)}
                  disabled={noModels || downloading}
                  aria-label={primaryLabel}
                >
                  <Download className={secondaryIconClass} />
                  {formatCount(downloadCount)}
                </button>
              </GuardedAction>
              <button
                type="button"
                className={secondaryItemClass}
                onClick={() => {
                  analytics.capture('tone.favorited');
                  favoriteTone();
                }}
                aria-label="Favorite tone"
              >
                <Bookmark
                  className={secondaryIconClass}
                  fill={isFavorite ? 'white' : 'none'}
                />
                {formatCount(favoriteCount)}
              </button>
              <Link href="#comments" className={secondaryItemClass} aria-label="Comment">
                <MessageCircle className={secondaryIconClass} />
                {commentsCount > 0 ? (
                  <span>
                    {formatCount(commentsCount)}{' '}
                    <span className="hidden">Comments</span>
                  </span>
                ) : (
                  <span className="hidden sm:inline">Comment</span>
                )}
              </Link>
              <button
                type="button"
                className={secondaryItemClass}
                onClick={() => {
                  analytics.capture('tone.shared', { isPrivate });
                  copyUrl();
                }}
                aria-label={shareLabel}
              >
                <Share className={secondaryIconClass} />
                <span className="hidden sm:inline">{shareLabel}</span>
              </button>
            </div>
          </>
        )}
      />
    </div>
  );
};

export default Actions;

const DownloadAllButton = ({
  tone,
  userId,
  models,
  isApiSelect = false,
  isOAuthFlow = false,
  isGearUnsupported = false,
  isArchitectureUnsupported = false,
  noA2Nam = false,
  selectGears,
  architectureFilter,
  onDownloadStart,
  forceDisabled = false,
}: {
  tone: ToneAndChildren;
  userId?: string;
  /**
   * Override `tone.models` for both the disabled-state check and the actual
   * download. Used by core-NAM where the tone page SSRs only the default
   * architecture's models and the segmented control lazy-fetches the rest
   * into a merged local list. Passing it ensures the button enables — and
   * the zip pulls the right files — for architectures fetched after SSR.
   */
  models?: ModelWithChildren[];
  isApiSelect?: boolean;
  isOAuthFlow?: boolean;
  isGearUnsupported?: boolean;
  isArchitectureUnsupported?: boolean;
  /** Core-only: NAM tone with no A2. Routes click to No-A2 modal. */
  noA2Nam?: boolean;
  selectGears?: string[];
  /**
   * Filter the downloaded model set by architecture. Used by the core-NAM
   * segmented-control-driven Download All (passes the selected segment).
   * Ignored by Select/embed branches in `downloadToneModels`.
   */
  architectureFilter?: ArchitectureVersion;
  /**
   * Fires when the click successfully initiates a download (post-disable
   * checks). Used by the core-NAM integrated Download All to trigger the
   * First-A2-download modal.
   */
  onDownloadStart?: () => void;
  /**
   * Passively disable the button (no dialog, no modal). Used by Select-NAM
   * when the user picks a segmented-control architecture the OAuth partner
   * can't accept.
   */
  forceDisabled?: boolean;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);
  const [showNoA2Modal, setShowNoA2Modal] = useState(false);
  const pluginDownload = usePluginDownloadIntercept(!isApiSelect);
  const searchParams = useSearchParams();
  // Effective model set: caller-supplied override (lazy-fetched merged list
  // on core-NAM) when present, otherwise the SSR'd `tone.models`.
  const effectiveModels = models ?? tone.models;
  const toneForDownload = models ? { ...tone, models } : tone;
  const handleDownload = async () => {
    onDownloadStart?.();
    setIsDownloading(true);
    try {
      await downloadToneModels({
        tone: toneForDownload,
        architectureFilter,
        onSuccess: async () => {
          const data = {
            tone_id: tone.id,
          } as { tone_id: number | null; user_id?: string };
          if (userId) {
            data.user_id = userId;
          }
          try {
            const response = await fetch('/api/downloads', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(data),
            });

            // ✅ IMPORTANT: Consume the response body
            if (!response.ok) {
              const errorData = await response.json();
              console.error('Failed to record download:', errorData);
            } else {
              // Even if you don't need the data, consume the body
              await response.json();
            }
          } catch (error) {
            console.error('Failed to record download:', error);
          }
        },
        isOAuthFlow: isApiSelect ? isOAuthFlow : false,
        authorizationId: searchParams?.get('authorization_id') ?? null,
      });
    } catch (error) {
      console.error('Failed to download models:', error);
      alert(`Failed to download models: ${error}`);
    }
    setIsDownloading(false);
  };

  const selectDialogActive = isGearUnsupported || isArchitectureUnsupported;
  const disabled = selectDialogActive || noA2Nam || forceDisabled;
  // For core: the downloadable set is filtered by `architectureFilter`. Show
  // the natively-disabled state when no models in that filter have a model_url.
  const hasDownloadableForFilter = architectureFilter
    ? effectiveModels?.some(
        (m) => m.architecture_version === architectureFilter && m.model_url
      )
    : effectiveModels?.some((m) => m.model_url);
  // Label suffix follows segmented control on core; Select shows plain
  // "Download" on desktop and icon-only on mobile.
  const archSuffix =
    architectureFilter === ARCHITECTURE_VERSION.A2
      ? ' A2'
      : architectureFilter === ARCHITECTURE_VERSION.A1
      ? ' A1'
      : architectureFilter === ARCHITECTURE_VERSION.CUSTOM
      ? ' Custom'
      : '';
  const fullLabel = isApiSelect ? 'Download' : `Download all${archSuffix}`;
  const shortLabel = archSuffix ? archSuffix.trim() : 'All';

  return (
    <>
      {selectDialogActive && showUnsupportedDialog && (
        <GearUnsupportedDialog
          selectGears={selectGears ?? []}
          isArchitectureUnsupported={isArchitectureUnsupported}
          onClose={() => setShowUnsupportedDialog(false)}
        />
      )}
      {noA2Nam && (
        <NoA2ModelsModal open={showNoA2Modal} onClose={() => setShowNoA2Modal(false)} />
      )}
      {pluginDownload.modal}
      <div
        className={disabled ? 'opacity-40' : undefined}
        onClick={
          selectDialogActive
            ? () => setShowUnsupportedDialog(true)
            : noA2Nam
            ? () => setShowNoA2Modal(true)
            : undefined
        }
      >
        <div className={disabled ? 'pointer-events-none' : undefined}>
          <Button
            isSecondary
            onClick={() => pluginDownload.intercept(handleDownload)}
            disabled={isDownloading || !hasDownloadableForFilter}
            style={{ height: 50, paddingLeft: 16, paddingRight: 16 }}
            className="self-start flex-shrink-0 whitespace-nowrap"
            tracking={{
              event: 'tone.downloaded',
              properties: {
                from: 'all_models',
                isApiSelect,
              },
            }}
            aria-label={fullLabel}
          >
            {isDownloading ? (
              <LoadingDots />
            ) : (
              <>
                <Download size={20} />
                <span className="hidden sm:inline">{fullLabel}</span>
                {!isApiSelect && (
                  <span className="inline sm:hidden">{shortLabel}</span>
                )}
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export { DownloadAllButton };
